/**
 * @file src/components/atoms/Tray/TrayFooter.tsx
 * @description This file defines the TrayFooter component, which serves as the footer section of a Tray component.
 * The TrayFooter component can display a variety of buttons and customizable content, making it a flexible part of the tray.
 * It ensures consistent styling and behavior across different instances of the tray footer.
 * @module TrayFooter
 */

import { Button } from '@/components';
import APP_CONFIG from '@/config/app';
import React from 'react';
import { TrayFooterProps } from './interfaces/Tray';

const visualizeElementsAndSpacing = false;
const borderClassToVisualizeElementsAndSpacing = visualizeElementsAndSpacing
  ? 'border-white border'
  : '';

const mainGap = APP_CONFIG.STYLES.MAIN_GAP;
const wholeStepDecrement = APP_CONFIG.STYLES.WHOLESTEP_DECREMENT;

const spacingConfig = {
  gap: `gap${mainGap}`,
  gapBetweenButtons: `gap${mainGap}${wholeStepDecrement}`,
};

const sizingConfig = {
  width: 'w-full',
  height: 'h-full',
  footerWidth: 'w-full',
  footerHeight: 'flex-shrink-0 h-full',
  footerContentWidth: 'w-full',
  footerContentHeight: 'h-full',
  footerButtonsContainerWidth: 'w-full',
  footerButtonsContainerHeight: 'h-full',
};

const TrayFooter: React.FC<TrayFooterProps> = ({
  footerContent,
  onClose,
  onContinue,
  extraButton1Label,
  extraButton1OnClick,
  extraButton1Icon,
  extraButton2Label,
  extraButton2OnClick,
  extraButton2Icon,
  extraButton1Props,
  extraButton2Props,
  includeCloseButton = true,
  includeContinueButton = false,
  closeButtonLabel = 'Close',
  continueButtonLabel = 'Continue',
}) => {
  const hasButtons =
    includeCloseButton ||
    includeContinueButton ||
    extraButton1Label ||
    extraButton2Label;

  return (
    <div
      className={`z-10 flex flex-col items-center 
        ${sizingConfig.width} 
        ${sizingConfig.height} 
        ${spacingConfig.gap} 
        ${borderClassToVisualizeElementsAndSpacing}`}
    >
      {footerContent && (
        <div
          className={`flex-1 
            ${sizingConfig.footerContentWidth} 
            ${sizingConfig.footerContentHeight} 
            ${borderClassToVisualizeElementsAndSpacing}`}
        >
          {footerContent}
        </div>
      )}
      {hasButtons && (
        <div
          className={`z-10 flex flex-row items-center 
            ${sizingConfig.footerButtonsContainerWidth} 
            ${sizingConfig.footerButtonsContainerHeight} 
            ${spacingConfig.gapBetweenButtons} 
            ${borderClassToVisualizeElementsAndSpacing}`}
        >
          {extraButton1Label && (
            <Button
              {...extraButton1Props}
              onClick={extraButton1OnClick}
              icon={extraButton1Icon}
              fullWidth={
                extraButton1Props?.fullWidth !== undefined
                  ? extraButton1Props.fullWidth
                  : true
              }
              iconStyles={extraButton1Props?.iconStyles || { intent: 'accent' }}
            >
              {extraButton1Label}
            </Button>
          )}
          {extraButton2Label && (
            <Button
              {...extraButton2Props}
              onClick={extraButton2OnClick}
              icon={extraButton2Icon}
              fullWidth={
                extraButton2Props?.fullWidth !== undefined
                  ? extraButton2Props.fullWidth
                  : true
              }
              iconStyles={extraButton2Props?.iconStyles || { intent: 'accent' }}
            >
              {extraButton2Label}
            </Button>
          )}
          {includeCloseButton && (
            <Button onClick={onClose} fullWidth>
              {closeButtonLabel}
            </Button>
          )}
          {includeContinueButton && (
            <Button onClick={onContinue} fullWidth>
              {continueButtonLabel}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default TrayFooter;
